import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  CreditCard, Smartphone, Mail, Upload, Loader2, CheckCircle, 
  DollarSign, MessageCircle, ArrowLeft
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { getSettings, confirmPayment } from '../lib/api';
import { useLanguage } from '../context/LanguageContext';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_josmart-staging/artifacts/r0wcvy09_file_00000000fee871f598adf429b9fe9c58.png";

const paymentMethods = [
  { id: 'paypal', name: 'PayPal', icon: '💳', field: 'paypal_email' },
  { id: 'stripe', name: 'Stripe / Card', icon: '💳', field: 'stripe_info' },
  { id: 'mpesa', name: 'M-Pesa', icon: '📱', field: 'mpesa_number' },
  { id: 'orange_money', name: 'Orange Money', icon: '📱', field: 'orange_money_number' },
  { id: 'airtel_money', name: 'Airtel Money', icon: '📱', field: 'airtel_money_number' },
  { id: 'etransfer', name: 'E-Transfer', icon: '📧', field: 'etransfer_email' },
];

const paymentTypes = {
  fr: [
    { value: 'website', label: 'Création de site web' },
    { value: 'app', label: 'Création d\'application' },
    { value: 'subscription', label: 'Abonnement application' },
    { value: 'service', label: 'Service' },
    { value: 'other', label: 'Autre' },
  ],
  en: [
    { value: 'website', label: 'Website creation' },
    { value: 'app', label: 'Application creation' },
    { value: 'subscription', label: 'App subscription' },
    { value: 'service', label: 'Service' },
    { value: 'other', label: 'Other' },
  ]
};

const apps = ['JoSmart Chat', 'JoSmart Edu', 'JoSmart Market', 'Autre'];

const currencies = ['USD', 'EUR', 'CAD', 'XAF', 'XOF', 'KES', 'NGN'];

export default function PaymentPage() {
  const { language } = useLanguage();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedMethod, setSelectedMethod] = useState(null);
  const [step, setStep] = useState(1); // 1: Choose method, 2: Fill form
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    payment_type: '',
    app_name: '',
    amount: '',
    currency: 'USD',
    payment_method: '',
    message: '',
    proof_image: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const data = await getSettings();
      setSettings(data);
    } catch (err) {
      console.error('Failed to load settings:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleMethodSelect = (method) => {
    setSelectedMethod(method);
    setFormData({ ...formData, payment_method: method.id });
    setStep(2);
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData({ ...formData, proof_image: reader.result });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      await confirmPayment(formData);
      setIsSuccess(true);
    } catch (err) {
      setError(language === 'fr' ? 'Une erreur est survenue. Veuillez réessayer.' : 'An error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const getMethodValue = (field) => {
    return settings?.[field] || null;
  };

  const availableMethods = paymentMethods.filter(m => getMethodValue(m.field));

  const texts = {
    fr: {
      title: 'Paiement',
      subtitle: 'Effectuez votre paiement en toute sécurité',
      step1Title: 'Choisissez votre méthode de paiement',
      step2Title: 'Confirmez votre paiement',
      payTo: 'Payez à :',
      backToMethods: 'Retour aux méthodes',
      formTitle: 'Informations de paiement',
      name: 'Nom complet',
      email: 'Email',
      paymentType: 'Type de paiement',
      selectType: 'Sélectionnez...',
      appName: 'Application concernée',
      selectApp: 'Sélectionnez l\'application...',
      amount: 'Montant payé',
      currency: 'Devise',
      message: 'Message (optionnel)',
      proofUpload: 'Preuve de paiement (capture d\'écran)',
      uploadImage: 'Cliquez pour télécharger',
      submit: 'Confirmer le paiement',
      submitting: 'Envoi en cours...',
      successTitle: 'Paiement envoyé !',
      successMessage: 'Votre confirmation de paiement a été envoyée. Nous allons vérifier et vous contacter bientôt.',
      backToHome: 'Retour à l\'accueil',
      noMethods: 'Aucune méthode de paiement configurée. Contactez-nous directement.',
    },
    en: {
      title: 'Payment',
      subtitle: 'Make your payment securely',
      step1Title: 'Choose your payment method',
      step2Title: 'Confirm your payment',
      payTo: 'Pay to:',
      backToMethods: 'Back to methods',
      formTitle: 'Payment information',
      name: 'Full name',
      email: 'Email',
      paymentType: 'Payment type',
      selectType: 'Select...',
      appName: 'Related application',
      selectApp: 'Select application...',
      amount: 'Amount paid',
      currency: 'Currency',
      message: 'Message (optional)',
      proofUpload: 'Payment proof (screenshot)',
      uploadImage: 'Click to upload',
      submit: 'Confirm payment',
      submitting: 'Sending...',
      successTitle: 'Payment sent!',
      successMessage: 'Your payment confirmation has been sent. We will verify and contact you soon.',
      backToHome: 'Back to home',
      noMethods: 'No payment methods configured. Contact us directly.',
    }
  };

  const t = texts[language];

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0B1521] flex items-center justify-center">
        <Loader2 size={40} className="animate-spin text-[#4A90D9]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0B1521]" data-testid="payment-page">
      <Navbar />
      
      <main className="pt-32 pb-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-12"
          >
            <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-[#4A90D9]/20 flex items-center justify-center">
              <CreditCard size={40} className="text-[#4A90D9]" />
            </div>
            <h1 className="text-3xl sm:text-4xl font-bold text-white mb-4">{t.title}</h1>
            <p className="text-lg text-white/70">{t.subtitle}</p>
          </motion.div>

          {isSuccess ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-[#152840] rounded-2xl p-12 text-center border border-white/10"
            >
              <CheckCircle size={64} className="mx-auto text-green-500 mb-6" />
              <h2 className="text-2xl font-bold text-white mb-4">{t.successTitle}</h2>
              <p className="text-white/70 mb-8">{t.successMessage}</p>
              <Link
                to="/"
                className="inline-flex items-center gap-2 px-6 py-3 bg-[#4A90D9] text-white rounded-full font-semibold hover:bg-[#5BA0E9] transition-colors"
              >
                <ArrowLeft size={20} />
                {t.backToHome}
              </Link>
            </motion.div>
          ) : (
            <>
              {/* Step 1: Choose Method */}
              {step === 1 && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  <h2 className="text-xl font-semibold text-white mb-6 text-center">{t.step1Title}</h2>
                  
                  {availableMethods.length > 0 ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                      {availableMethods.map((method) => (
                        <motion.button
                          key={method.id}
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                          onClick={() => handleMethodSelect(method)}
                          className="bg-[#152840] rounded-xl p-6 border border-white/10 hover:border-[#4A90D9]/50 transition-colors text-left"
                          data-testid={`payment-method-${method.id}`}
                        >
                          <span className="text-3xl mb-3 block">{method.icon}</span>
                          <h3 className="text-lg font-semibold text-white mb-2">{method.name}</h3>
                          <p className="text-white/60 text-sm">{getMethodValue(method.field)}</p>
                        </motion.button>
                      ))}
                    </div>
                  ) : (
                    <div className="bg-[#152840] rounded-xl p-8 text-center border border-white/10">
                      <p className="text-white/70">{t.noMethods}</p>
                    </div>
                  )}
                </motion.div>
              )}

              {/* Step 2: Payment Form */}
              {step === 2 && selectedMethod && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  <button
                    onClick={() => setStep(1)}
                    className="flex items-center gap-2 text-white/60 hover:text-white mb-6 transition-colors"
                  >
                    <ArrowLeft size={20} />
                    {t.backToMethods}
                  </button>

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    {/* Payment Info */}
                    <div className="bg-[#152840] rounded-2xl p-6 border border-white/10">
                      <h3 className="text-lg font-semibold text-white mb-4">{t.step2Title}</h3>
                      <div className="bg-[#0B1521] rounded-xl p-4 mb-4">
                        <p className="text-white/60 text-sm mb-2">{t.payTo}</p>
                        <div className="flex items-center gap-3">
                          <span className="text-2xl">{selectedMethod.icon}</span>
                          <div>
                            <p className="font-semibold text-white">{selectedMethod.name}</p>
                            <p className="text-[#4A90D9]">{getMethodValue(selectedMethod.field)}</p>
                          </div>
                        </div>
                      </div>
                      <p className="text-white/60 text-sm">
                        {language === 'fr' 
                          ? '1. Effectuez votre paiement avec les informations ci-dessus\n2. Prenez une capture d\'écran de la confirmation\n3. Remplissez le formulaire ci-contre'
                          : '1. Make your payment with the information above\n2. Take a screenshot of the confirmation\n3. Fill out the form below'
                        }
                      </p>
                    </div>

                    {/* Confirmation Form */}
                    <div className="bg-[#152840] rounded-2xl p-6 border border-white/10">
                      <h3 className="text-lg font-semibold text-white mb-4">{t.formTitle}</h3>
                      
                      <form onSubmit={handleSubmit} className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-white/80 text-sm mb-2">{t.name} *</label>
                            <input
                              type="text"
                              value={formData.name}
                              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                              required
                              className="w-full px-4 py-3 rounded-lg input-dark"
                              data-testid="payment-input-name"
                            />
                          </div>
                          <div>
                            <label className="block text-white/80 text-sm mb-2">{t.email} *</label>
                            <input
                              type="email"
                              value={formData.email}
                              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                              required
                              className="w-full px-4 py-3 rounded-lg input-dark"
                              data-testid="payment-input-email"
                            />
                          </div>
                        </div>

                        <div>
                          <label className="block text-white/80 text-sm mb-2">{t.paymentType} *</label>
                          <select
                            value={formData.payment_type}
                            onChange={(e) => setFormData({ ...formData, payment_type: e.target.value })}
                            required
                            className="w-full px-4 py-3 rounded-lg input-dark bg-[#152840] text-white"
                            data-testid="payment-select-type"
                          >
                            <option value="">{t.selectType}</option>
                            {paymentTypes[language].map((type) => (
                              <option key={type.value} value={type.value}>{type.label}</option>
                            ))}
                          </select>
                        </div>

                        {(formData.payment_type === 'subscription' || formData.payment_type === 'app') && (
                          <div>
                            <label className="block text-white/80 text-sm mb-2">{t.appName}</label>
                            <select
                              value={formData.app_name}
                              onChange={(e) => setFormData({ ...formData, app_name: e.target.value })}
                              className="w-full px-4 py-3 rounded-lg input-dark bg-[#152840] text-white"
                            >
                              <option value="">{t.selectApp}</option>
                              {apps.map((app) => (
                                <option key={app} value={app}>{app}</option>
                              ))}
                            </select>
                          </div>
                        )}

                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-white/80 text-sm mb-2">{t.amount} *</label>
                            <input
                              type="text"
                              value={formData.amount}
                              onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                              required
                              placeholder="100"
                              className="w-full px-4 py-3 rounded-lg input-dark"
                              data-testid="payment-input-amount"
                            />
                          </div>
                          <div>
                            <label className="block text-white/80 text-sm mb-2">{t.currency}</label>
                            <select
                              value={formData.currency}
                              onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                              className="w-full px-4 py-3 rounded-lg input-dark bg-[#152840] text-white"
                            >
                              {currencies.map((c) => (
                                <option key={c} value={c}>{c}</option>
                              ))}
                            </select>
                          </div>
                        </div>

                        <div>
                          <label className="block text-white/80 text-sm mb-2">{t.message}</label>
                          <textarea
                            value={formData.message}
                            onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                            rows={3}
                            className="w-full px-4 py-3 rounded-lg input-dark resize-none"
                          />
                        </div>

                        <div>
                          <label className="block text-white/80 text-sm mb-2">{t.proofUpload}</label>
                          <div className="relative">
                            <input
                              type="file"
                              accept="image/*"
                              onChange={handleImageUpload}
                              className="hidden"
                              id="proof-upload"
                            />
                            <label
                              htmlFor="proof-upload"
                              className="flex items-center justify-center gap-2 w-full px-4 py-8 rounded-lg border-2 border-dashed border-white/20 hover:border-[#4A90D9]/50 cursor-pointer transition-colors"
                            >
                              {formData.proof_image ? (
                                <img 
                                  src={formData.proof_image} 
                                  alt="Proof" 
                                  className="max-h-32 rounded-lg"
                                />
                              ) : (
                                <>
                                  <Upload size={24} className="text-white/60" />
                                  <span className="text-white/60">{t.uploadImage}</span>
                                </>
                              )}
                            </label>
                          </div>
                        </div>

                        {error && (
                          <p className="text-red-400 text-sm">{error}</p>
                        )}

                        <button
                          type="submit"
                          disabled={isSubmitting}
                          className="w-full py-3 bg-[#4A90D9] text-white rounded-full font-semibold hover:bg-[#5BA0E9] transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                          data-testid="payment-submit-btn"
                        >
                          {isSubmitting ? (
                            <>
                              <Loader2 size={20} className="animate-spin" />
                              {t.submitting}
                            </>
                          ) : (
                            <>
                              <CheckCircle size={20} />
                              {t.submit}
                            </>
                          )}
                        </button>
                      </form>
                    </div>
                  </div>
                </motion.div>
              )}
            </>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}
